import * as React from "react";
import { cn } from "../lib";

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
}

const Modal = ({
  open,
  onOpenChange,
  children,
  className,
  overlayClassName,
  contentClassName,
}: ModalProps) => {
  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center p-4",
        className,
      )}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close modal"
        onClick={() => onOpenChange(false)}
        className={cn("absolute inset-0 bg-black/40", overlayClassName)}
      />
      <div
        className={cn(
          "relative flex max-h-[85vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[24px] bg-[#F7F8FD] shadow-[0_24px_60px_rgba(15,20,40,0.25)]",
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
};

const ModalHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex items-start justify-between px-6 pt-6", className)}
    {...props}
  />
);

const ModalTitle = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h2
    className={cn("text-[24px] font-[500] text-[#161827]", className)}
    {...props}
  >
    {children ?? <span className="sr-only">Modal title</span>}
  </h2>
);

const ModalBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex-1 overflow-y-auto px-6 py-6", className)}
    {...props}
  />
);

const ModalFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex items-center justify-end gap-4 border-t border-[#E6E9F5] px-6 py-4",
      className,
    )}
    {...props}
  />
);

export { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter };
